'use client'

import PropTypes from 'prop-types'
import MUIInput from '@mui/material/TextField'
import MUIStack from '@mui/material/Stack'
import MUIInputAdornment from '@mui/material/InputAdornment'

export const TextEntry = ({
  value = '',
  onChange = (val) => console.log(val),
  onKeyDown,
  onKeyUp,
  label = 'Description',
  required,
  onBlur = () => {},
  limit = 524288, // fun fact: 524288 is the default number of chars allowed in an HTML input field
  isNumber = false,
  multiline = false,
  isDataGridFilter = false,
  disabled = false,
  min = 0,
  max = 10000000000000,
  decimalPlaces = 0,
  adornment = '',
  endAdornment = '',
  autoFocus = false,
  sx = {},
  errors = {},
  showErrorInLabel = true,
  size = 'small',
  name,
  type = 'text',
  readOnly = false,
  helperText = '',
  placeholder = '',
  ...props
}) => {
  const handleKeyDown = (e) => {
    if (onKeyDown) {
      onKeyDown(e)
    }
  }

  const handleKeyUp = (e) => {
    if (onKeyUp) {
      onKeyUp(e)
    }
  }

  if (isNumber) {
    let step = decimalPlaces ? 0.01 : 1

    return (
      <MUIInput
        data-testid="TextEntry"
        size={size}
        variant="outlined"
        sx={{
          minWidth: '150px',
          textAlign: 'right',
          '.MuiInputBase-root': {
            m: 0,
            minHeight: '40px',
            paddingLeft: 0,
            paddingRight: endAdornment ? 0 : '14px'
          },
          ...sx
        }}
        value={value || value === 0 || value === '0' ? value : ''}
        label={showErrorInLabel && errors && errors[name] ? errors[name] : label}
        onChange={(e) =>
          onChange(e.target.value === '' || e.target.value === undefined ? null : Number(e.target.value))
        }
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        required={required}
        disabled={disabled}
        autoFocus={autoFocus}
        readOnly={readOnly}
        name={name}
        error={errors && errors[name] ? true : false}
        helperText={helperText}
        inputProps={{ type: 'number', min, max, step }}
        InputProps={{
          startAdornment: adornment ? <Adornment position="start" content={adornment} size={size} /> : null,
          endAdornment: endAdornment ? <Adornment position="end" content={endAdornment} size={size} /> : null,
          readOnly: readOnly
        }}
      />
    )
  }

  if (isDataGridFilter) {
    return (
      <MUIInput
        data-testid="TextEntry"
        size={size}
        variant="outlined"
        sx={{
          minWidth: '150px',
          textAlign: 'right',
          '.MuiInputBase-root': {
            m: 0,
            minHeight: '40px',
            paddingLeft: 0,
            paddingRight: endAdornment ? 0 : '14px'
          },
          ...sx
        }}
        value={value}
        label={showErrorInLabel && errors && errors[name] ? errors[name] : label}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => {
          const trimmed = e.target.value.trim()
          const parsed = trimmed ? Number(trimmed) : null
          if (onBlur) onBlur(!isNaN(parsed) ? parsed : null)
        }}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoFocus={autoFocus}
        readOnly={readOnly}
        name={name}
        error={errors && errors[name] ? true : false}
        helperText={helperText}
        inputProps={{ type: 'text' }}
        InputProps={{
          startAdornment: adornment ? <Adornment position="start" content={adornment} size={size} /> : null,
          endAdornment: endAdornment ? <Adornment position="end" content={endAdornment} size={size} /> : null,
          readOnly: readOnly
        }}
      />
    )
  }

  return (
    <MUIInput
      data-testid="TextEntry"
      multiline
      rows={multiline ? undefined : 1}
      size={size}
      sx={{
        width: '100%',
        minHeight: '40px',
        '.MuiInputBase-root': {
          m: 0,
          minHeight: '40px',
          paddingLeft: adornment ? 0 : '14px',
          paddingRight: endAdornment ? 0 : '14px',
          'textarea::placeholder': { color: 'rgba(5, 28, 51, 0.60)', opacity: 1 }
        },
        ...sx
      }}
      value={value || ''}
      label={showErrorInLabel && errors && errors[name] ? errors[name] : label}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      required={required}
      disabled={disabled}
      autoFocus={autoFocus}
      readOnly={readOnly}
      placeholder={label ? '' : placeholder}
      name={name}
      type={type}
      error={errors && errors[name] ? true : false}
      helperText={helperText}
      inputProps={{
        type,
        maxLength: limit
      }}
      InputProps={{
        startAdornment: adornment ? (
          <Adornment position="start" content={adornment} size={size} fullHeight={multiline} />
        ) : null,
        readOnly: readOnly
      }}
      {...props}
    />
  )
}

TextEntry.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  limit: PropTypes.number,
  isNumber: PropTypes.bool,
  multiline: PropTypes.bool,
  min: PropTypes.number,
  max: PropTypes.number,
  sx: PropTypes.object,
  adornment: PropTypes.any,
  endAdornment: PropTypes.string,
  autoFocus: PropTypes.bool,
  errors: PropTypes.object,
  showErrorInLabel: PropTypes.bool,
  size: PropTypes.string,
  name: PropTypes.string,
  type: PropTypes.string,
  readOnly: PropTypes.bool,
  helperText: PropTypes.string,
  decimalPlaces: PropTypes.number,
  onBlur: PropTypes.func,
  isDataGridFilter: PropTypes.bool,
  placeholder: PropTypes.string
}

const Adornment = ({ position = 'start', content = '£', size, fullHeight }) => {
  return (
    <MUIInputAdornment
      position={position}
      sx={{ height: fullHeight ? 'calc(100% + 17px)' : 'auto', maxHeight: fullHeight ? 'calc(100% + 17px)' : '2em' }}
    >
      <MUIStack
        direction="row"
        alignItems="center"
        justifyContent="center"
        sx={{
          width: size === 'small' ? '40px' : '53px',
          height: fullHeight ? '100%' : size === 'small' ? '40px' : '53px',
          backgroundColor: 'rgba(61, 81, 123, 0.04)',
          userSelect: 'none',
          pointerEvents: 'none',
          borderLeft: position === 'end' ? '1px solid rgba(0, 0, 0, 0.23)' : 'none',
          borderRight: position === 'start' ? '1px solid rgba(0, 0, 0, 0.23)' : 'none',
          borderRadius: position === 'start' ? '4px 0px 0px 4px' : '0px 4px 4px 0px'
        }}
      >
        {content}
      </MUIStack>
    </MUIInputAdornment>
  )
}

Adornment.propTypes = {
  position: PropTypes.string,
  content: PropTypes.any,
  size: PropTypes.string,
  fullHeight: PropTypes.bool
}
